/** @jsx jsx */
import React, { useMemo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { getEmojiProvider } from '@jira/platform__atlaskit-emoji-provider/src/emoji.tsx';
import type { SmartLinkResponse } from '@atlaskit/linking-types';
import { ResourcedEmoji } from '@atlaskit/emoji';
import { useIntl } from '@atlassian/jira-intl';
import {
	SimpleInlineSmartLink,
	type Props,
} from '@atlassian/jira-issue-field-smart-link/src/ui/simple-inline-smart-link/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import restrictedMessages from '../../restricted/messages.tsx';
import { generatePlaceholderData } from './generate-placeholder-data.tsx';

const styles = cssMap({
	emojiWrapper: {
		// Fixes vertical alignment issue with ResourcedEmoji inside Smart Link
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> span': {
			display: 'flex',
			alignItems: 'center',
		},
		// Improves VC as default value is auto which causes a mutation
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'img[data-vc="emoji"]': {
			width: '12px',
		},
	},
});

export type InlineSmartLinkProps = Props & {
	/** Short hand name @example :grinning: */
	emojiName?: string;
};

/**
 * A wrapper around SimpleInlineSmartLink from @atlassian/jira-issue-field-smart-link to provide common placeholder data for Atlas Project links.
 *
 * Note: This is intended to replace the existing ./InlineSmartLink component.
 * Move these changes to ./InlineSmartLink as part of anip-217_use_common_smartlink_for_atlas_projects feature flag cleanup.
 */
export const InlineSmartLink = ({
	url,
	text,
	emojiName,
	icon,
	...otherProps
}: InlineSmartLinkProps) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const placeholderData = useMemo<SmartLinkResponse | undefined>(() => {
		if (!url) {
			return undefined;
		}

		return generatePlaceholderData(url, formatMessage(restrictedMessages.restrictedProject), text);
	}, [formatMessage, text, url]);

	// Copied from ./InlineSmartLink.tsx:161
	const emojiProvider = useMemo(() => getEmojiProvider(), []);
	const emoji = useMemo(() => {
		if (emojiProvider && emojiName) {
			const emojiUri = emojiName.startsWith(':') ? emojiName.slice(1, -1) : emojiName;

			return (
				<div css={styles.emojiWrapper}>
					<ResourcedEmoji
						emojiProvider={emojiProvider}
						emojiId={{ shortName: emojiName }}
						fitToHeight={12}
						optimisticImageURL={`/gateway/api/emoji/${cloudId}/${emojiUri}/path?scale=MDPI`}
					/>
				</div>
			);
		}
	}, [cloudId, emojiName, emojiProvider]);

	return (
		<SimpleInlineSmartLink
			{...otherProps}
			url={url}
			text={text}
			// Order matters, icon takes precedence over emoji
			icon={icon || emoji}
			isSimplifiedUI
			placeholderData={placeholderData}
		/>
	);
};
